import type {
  ClaimCustomerManagerResponse,
  CustomerManagerCustomer360Response,
  CustomerManagerCustomerPoolResponse,
  CustomerManagerIssueGiftRequest,
  CustomerManagerIssueGiftResponse,
  CustomerManagerMoneyResponse,
  CustomerManagerMoneyTrendDays,
  CustomerManagerPortfolioRequestLogResponse,
  CustomerManagerRedeemGiftCardResponse,
  CustomerManagerRequestLifecycleResponse,
  CustomerManagerRequestLogResponse,
  CustomerManagerRevokeApiKeyResponse,
  CustomerManagerPreciseRunwayResponse,
  CustomerManagerRunwayResponse,
  CustomerManagerUpdateSkuRateResponse,
  CustomerManagerUpdateSkuVisibilityResponse,
  CustomerManagerWorkbenchResponse,
  ListAdminCreditLotsResponse,
  SalesUpdateAccountNamespaceRateResponse,
  SetServiceSuspensionRequest,
  SetServiceSuspensionResponse,
  UpdateAccountNamespaceRateResponse,
  UpdateOrgNamespaceVisibilityResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { APPROVALS_ROOT_KEY } from './approvals.ts';
import { OpsApiError, opsApiRequest } from './client.ts';
import { CUSTOMER_OWNERSHIP_ROOT_KEY } from './customerOwnership.ts';

const BASE = '/v1/ops/v2/customer-manager';
export const CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY = ['customer-manager-workspace'] as const;

/**
 * Column sort for the two portfolio lists (#1938). Both are server-paginated,
 * so the sort has to travel with the request: ordering only the fifty rows
 * already on screen would make the first page a claim about the page rather
 * than about the book. `sort` absent = the route's own default order.
 */
export type CustomerPortfolioSortKey =
  | 'email'
  | 'org'
  | 'created_at'
  | 'balance'
  | 'spend_30d'
  | 'requests'
  | 'keys'
  | 'owner'
  | 'risks';

export interface CustomerManagerWorkbenchScope {
  readonly limit: number;
  readonly offset: number;
  readonly email: string;
  /**
   * On the pool the filter is honoured for admin, finance and self-scoped
   * customer managers (ops-api forwards it; it can only narrow); it is
   * ignored for sales leads, whose pool stays pinned to their profile
   * brand (#1540). On the workbench it remains admin-only.
   */
  readonly brand?: string;
  readonly sort?: CustomerPortfolioSortKey;
  readonly dir?: 'asc' | 'desc';
}

export interface CustomerManagerRequestLogScope {
  readonly limit: number;
  readonly offset: number;
  readonly from: string;
  readonly to: string;
  readonly status: '' | 'success' | 'error';
  readonly lifecycleStatus: string;
  readonly requestId: string;
  readonly apiKeyId: string;
  readonly channelId: string;
  readonly channelSlug: string;
}

export interface CustomerManagerRevokeApiKeyRequest {
  readonly reason: string;
}


export interface CustomerManagerRedeemGiftCardRequest {
  readonly code: string;
  readonly external_request_id?: string;
  readonly reason?: string;
  readonly metadata?: Record<string, unknown>;
}

export interface CustomerManagerUpdateSkuRateRequest {
  readonly sku_id: string;
  readonly rate_multiplier: number | null;
  readonly note?: string | null;
}

export interface CustomerManagerUpdateSkuVisibilityRequest {
  readonly visible: boolean;
}

/**
 * Line-grain (namespace) rate write — the grain a customer manager actually
 * negotiates at (2026-07-31 design §5.2). `rate_multiplier: null` clears the
 * agreement and drops the line back to the platform list price.
 */
export interface CustomerManagerUpdateNamespaceRateRequest {
  readonly namespace_id: string;
  readonly rate_multiplier: number | null;
  readonly note?: string | null;
}

export interface CustomerManagerUpdateNamespaceVisibilityRequest {
  readonly visible: boolean;
}

/**
 * One model a proposed line rate would push below its floor, as carried by a
 * `NAMESPACE_RATE_BELOW_SKU_FLOOR` (409) refusal.
 *
 * `floor_multiplier` is already tier-reduced server-side: a customer-manager
 * session receives the sales floor or `null`, never a procurement-derived
 * number. The console does not re-derive it, so it cannot re-introduce the leak.
 */
export interface NamespaceRateFloorBreach {
  readonly sku_id: string;
  readonly sku_name: string;
  readonly floor_multiplier: number | null;
}

/**
 * Narrow the breach list out of a 409 refusal. A bare refusal on a wide line is
 * unactionable — the operator has to be told which models to price individually
 * — so the names ride in the error envelope and are read back here.
 *
 * Returns null for every other error, and for a malformed list, so the caller
 * falls through to ordinary error text rather than rendering a partial table.
 * Same access shape as `cascadeImpactFromError`: ops-console's client assigns
 * the whole parsed `error` object to `details`.
 */
export function namespaceFloorBreachesFromError(
  err: unknown,
): readonly NamespaceRateFloorBreach[] | null {
  if (!(err instanceof OpsApiError) || err.code !== 'NAMESPACE_RATE_BELOW_SKU_FLOOR') {
    return null;
  }
  const raw = err.details?.['breaches'];
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const breaches: NamespaceRateFloorBreach[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) return null;
    const row: Record<string, unknown> = { ...item };
    const skuId = row['sku_id'];
    const skuName = row['sku_name'];
    const floor = row['floor_multiplier'];
    if (typeof skuId !== 'string' || typeof skuName !== 'string') return null;
    if (floor !== null && typeof floor !== 'number') return null;
    breaches.push({ sku_id: skuId, sku_name: skuName, floor_multiplier: floor });
  }
  return breaches;
}

function buildQueryString(query: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    const stringValue = typeof value === 'number' ? value.toString() : value;
    if (stringValue.length > 0) {
      params.set(key, stringValue);
    }
  }
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

export function customerManagerWorkbenchQueryKey(
  scope: CustomerManagerWorkbenchScope,
): readonly ['customer-manager-workspace', 'workbench', CustomerManagerWorkbenchScope] {
  return ['customer-manager-workspace', 'workbench', scope] as const;
}

export function customerManagerPoolQueryKey(
  scope: CustomerManagerWorkbenchScope,
): readonly ['customer-manager-workspace', 'customer-pool', CustomerManagerWorkbenchScope] {
  return ['customer-manager-workspace', 'customer-pool', scope] as const;
}

export function customerManagerRequestLogsQueryKey(
  orgId: string,
  scope: CustomerManagerRequestLogScope,
): readonly [
  'customer-manager-workspace',
  'request-logs',
  string,
  CustomerManagerRequestLogScope,
] {
  return ['customer-manager-workspace', 'request-logs', orgId, scope] as const;
}

export function customerManagerPortfolioRequestLogsQueryKey(
  scope: CustomerManagerRequestLogScope,
): readonly [
  'customer-manager-workspace',
  'portfolio-request-logs',
  CustomerManagerRequestLogScope,
] {
  return ['customer-manager-workspace', 'portfolio-request-logs', scope] as const;
}

export async function getCustomerManagerWorkbench(
  scope: CustomerManagerWorkbenchScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerWorkbenchResponse> {
  const qs = buildQueryString({
    limit: scope.limit,
    offset: scope.offset,
    email: scope.email.trim(),
    brand: scope.brand,
    sort: scope.sort,
    dir: scope.dir,
  });
  return opsApiRequest<CustomerManagerWorkbenchResponse>(
    `${BASE}/workbench${qs}`,
    { signal },
    fetchImpl,
  );
}

/**
 * Workbench money view (epic #1035, #1065). Scope is enforced
 * server-side (CM = own book, lead = own brand); the only client
 * input is the trend window selector. Admin sessions may pass an
 * optional `ownerOpsUserId` / `brand` drill-down.
 */
export interface CustomerManagerMoneyScope {
  readonly days: CustomerManagerMoneyTrendDays;
  /** Admin-only drill-down; ignored server-side for sales sessions. */
  readonly brand?: string;
}

export function customerManagerMoneyQueryKey(
  scope: CustomerManagerMoneyScope,
): readonly ['customer-manager-workspace', 'money', CustomerManagerMoneyScope] {
  return ['customer-manager-workspace', 'money', scope] as const;
}

export async function getCustomerManagerMoney(
  scope: CustomerManagerMoneyScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerMoneyResponse> {
  const qs = buildQueryString({
    days: scope.days,
    brand: scope.brand,
  });
  return opsApiRequest<CustomerManagerMoneyResponse>(`${BASE}/money${qs}`, { signal }, fetchImpl);
}

export function useCustomerManagerMoney(
  scope: CustomerManagerMoneyScope,
): UseQueryResult<CustomerManagerMoneyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: customerManagerMoneyQueryKey(scope),
    queryFn: ({ signal }) => getCustomerManagerMoney(scope, fetchImpl, signal),
  });
}

/**
 * Workbench 催充值 runway list (epic #1035 E6-D2, #1066). Scope is
 * enforced server-side (CM = own book, lead = brand book); the
 * endpoint returns the WHOLE owned book, so the panel's days-left
 * threshold filters client-side without refetching. No client scope
 * params exist by design — ops-api ignores them for sales sessions.
 */
export function customerManagerRunwayQueryKey(): readonly [
  'customer-manager-workspace',
  'runway',
] {
  return ['customer-manager-workspace', 'runway'] as const;
}

export async function getCustomerManagerRunway(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerRunwayResponse> {
  return opsApiRequest<CustomerManagerRunwayResponse>(`${BASE}/runway`, { signal }, fetchImpl);
}

export function useCustomerManagerRunway(): UseQueryResult<CustomerManagerRunwayResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: customerManagerRunwayQueryKey(),
    queryFn: ({ signal }) => getCustomerManagerRunway(fetchImpl, signal),
  });
}

/**
 * Customer 360 precise runway (#2155). Separate fetcher and return type
 * from {@link getCustomerManagerRunway}: feeding this payload into the
 * workbench chase-list renderer is a compile error (`mode` and
 * `days_left: number | null` do not match the list contract).
 */
export function customerManagerCustomerRunwayQueryKey(
  accountId: string,
  burnDays?: number,
): readonly ['customer-manager-workspace', 'runway', 'account', string, number | undefined] {
  return ['customer-manager-workspace', 'runway', 'account', accountId, burnDays] as const;
}

export async function getCustomerManagerCustomerRunway(
  accountId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
  burnDays?: number,
): Promise<CustomerManagerPreciseRunwayResponse> {
  const qs = buildQueryString({ accountId, burnDays });
  return opsApiRequest<CustomerManagerPreciseRunwayResponse>(
    `${BASE}/runway${qs}`,
    { signal },
    fetchImpl,
  );
}

export function useCustomerManagerCustomerRunway(
  accountId: string | null,
  burnDays: number,
  enabled: boolean,
): UseQueryResult<CustomerManagerPreciseRunwayResponse> {
  const { fetchImpl } = useSession();
  const id = accountId ?? '';
  return useQuery({
    queryKey: customerManagerCustomerRunwayQueryKey(id, burnDays),
    queryFn: ({ signal }) => getCustomerManagerCustomerRunway(id, fetchImpl, signal, burnDays),
    enabled: enabled && id.length > 0,
  });
}

type PreciseRunwayIsNotList = CustomerManagerPreciseRunwayResponse extends CustomerManagerRunwayResponse
  ? 'precise runway must not be assignable to the chase-list response'
  : true;
const preciseRunwayIsNotList: PreciseRunwayIsNotList = true;
void preciseRunwayIsNotList;

export async function getCustomerManagerCustomer360(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerCustomer360Response> {
  return opsApiRequest<CustomerManagerCustomer360Response>(
    `${BASE}/customers/${encodeURIComponent(orgId)}`,
    { signal },
    fetchImpl,
  );
}

export async function getCustomerManagerRequestLogs(
  orgId: string,
  scope: CustomerManagerRequestLogScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerRequestLogResponse> {
  const qs = buildQueryString({
    limit: scope.limit,
    offset: scope.offset,
    from: scope.from,
    to: scope.to,
    status: scope.status,
    lifecycleStatus: scope.lifecycleStatus.trim(),
    requestId: scope.requestId.trim(),
    apiKeyId: scope.apiKeyId,
    channelId: scope.channelId.trim(),
    channelSlug: scope.channelSlug.trim(),
  });
  return opsApiRequest<CustomerManagerRequestLogResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/requests${qs}`,
    { signal },
    fetchImpl,
  );
}

export async function getCustomerManagerPortfolioRequestLogs(
  scope: CustomerManagerRequestLogScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerPortfolioRequestLogResponse> {
  const qs = buildQueryString({
    limit: scope.limit,
    offset: scope.offset,
    from: scope.from,
    to: scope.to,
    status: scope.status,
    lifecycleStatus: scope.lifecycleStatus.trim(),
    requestId: scope.requestId.trim(),
    apiKeyId: scope.apiKeyId.trim(),
    channelId: scope.channelId.trim(),
    channelSlug: scope.channelSlug.trim(),
  });
  return opsApiRequest<CustomerManagerPortfolioRequestLogResponse>(
    `${BASE}/requests${qs}`,
    { signal },
    fetchImpl,
  );
}

export async function getCustomerManagerRequestLifecycle(
  orgId: string,
  requestId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerRequestLifecycleResponse> {
  return opsApiRequest<CustomerManagerRequestLifecycleResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/requests/${encodeURIComponent(requestId)}/lifecycle`,
    { signal },
    fetchImpl,
  );
}

/**
 * The credit lots still holding money on this customer's account, in the order
 * a charge will consume them — the operator's side of the gift balance the
 * tenant sees on their own billing page. Read-only: lots are produced by the
 * gift/top-up paths and retired by charges and the expiry sweep, never edited
 * from here.
 */
export async function getCustomerManagerCreditLots(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListAdminCreditLotsResponse> {
  return opsApiRequest<ListAdminCreditLotsResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/credit-lots`,
    { signal },
    fetchImpl,
  );
}

export async function getCustomerManagerCustomerPool(
  scope: CustomerManagerWorkbenchScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CustomerManagerCustomerPoolResponse> {
  const qs = buildQueryString({
    limit: scope.limit,
    offset: scope.offset,
    email: scope.email.trim(),
    brand: scope.brand,
    sort: scope.sort,
    dir: scope.dir,
  });
  return opsApiRequest<CustomerManagerCustomerPoolResponse>(
    `${BASE}/customer-pool${qs}`,
    { signal },
    fetchImpl,
  );
}

export async function claimCustomerFromPool(
  orgId: string,
  fetchImpl: typeof fetch,
): Promise<ClaimCustomerManagerResponse> {
  return opsApiRequest<ClaimCustomerManagerResponse>(
    `${BASE}/customer-pool/${encodeURIComponent(orgId)}/claim`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function revokeCustomerManagerApiKey(
  orgId: string,
  keyId: string,
  body: CustomerManagerRevokeApiKeyRequest,
  fetchImpl: typeof fetch,
): Promise<CustomerManagerRevokeApiKeyResponse> {
  return opsApiRequest<CustomerManagerRevokeApiKeyResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/api-keys/${encodeURIComponent(keyId)}/revoke`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function issueCustomerManagerGift(
  orgId: string,
  body: CustomerManagerIssueGiftRequest,
  fetchImpl: typeof fetch,
): Promise<CustomerManagerIssueGiftResponse> {
  return opsApiRequest<CustomerManagerIssueGiftResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/gifts/issue`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function redeemCustomerManagerGiftCard(
  orgId: string,
  body: CustomerManagerRedeemGiftCardRequest,
  fetchImpl: typeof fetch,
): Promise<CustomerManagerRedeemGiftCardResponse> {
  return opsApiRequest<CustomerManagerRedeemGiftCardResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/gift-cards/redeem-on-behalf`,
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * Per-customer service kill switch (epic #1033 E4-C7, #1131). Note this route
 * lives under `/v1/ops/customers` (admin + sales-lead), NOT the CM-inclusive
 * `/v1/ops/v2/customer-manager` base.
 */
export async function setCustomerServiceSuspension(
  orgId: string,
  body: SetServiceSuspensionRequest,
  fetchImpl: typeof fetch,
): Promise<SetServiceSuspensionResponse> {
  return opsApiRequest<SetServiceSuspensionResponse>(
    `/v1/ops/customers/${encodeURIComponent(orgId)}/service-suspension`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function updateCustomerManagerSkuRate(
  orgId: string,
  body: CustomerManagerUpdateSkuRateRequest,
  fetchImpl: typeof fetch,
): Promise<CustomerManagerUpdateSkuRateResponse> {
  return opsApiRequest<CustomerManagerUpdateSkuRateResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/sku-rates`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function updateCustomerManagerSkuVisibility(
  orgId: string,
  skuId: string,
  body: CustomerManagerUpdateSkuVisibilityRequest,
  fetchImpl: typeof fetch,
): Promise<CustomerManagerUpdateSkuVisibilityResponse> {
  return opsApiRequest<CustomerManagerUpdateSkuVisibilityResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/sku-visibility/${encodeURIComponent(skuId)}`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function updateCustomerManagerNamespaceRate(
  orgId: string,
  body: CustomerManagerUpdateNamespaceRateRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateAccountNamespaceRateResponse | SalesUpdateAccountNamespaceRateResponse> {
  return opsApiRequest<UpdateAccountNamespaceRateResponse | SalesUpdateAccountNamespaceRateResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/namespace-rates`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function updateCustomerManagerNamespaceVisibility(
  orgId: string,
  namespaceId: string,
  body: CustomerManagerUpdateNamespaceVisibilityRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateOrgNamespaceVisibilityResponse> {
  return opsApiRequest<UpdateOrgNamespaceVisibilityResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/namespace-visibility/${encodeURIComponent(namespaceId)}`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

/**
 * A line-rate write moves the resolved rate of EVERY model on the line that
 * carries no exception, so the whole customer detail is refetched rather than
 * the written row patched in. The root key already includes the detail key;
 * issuing both back-to-back cancels the first refetch (see the SKU-visibility
 * hook below).
 */
export function useUpdateCustomerManagerNamespaceRate(): UseMutationResult<
  UpdateAccountNamespaceRateResponse | SalesUpdateAccountNamespaceRateResponse,
  Error,
  { orgId: string; body: CustomerManagerUpdateNamespaceRateRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => updateCustomerManagerNamespaceRate(orgId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
    },
  });
}

export function useUpdateCustomerManagerNamespaceVisibility(): UseMutationResult<
  UpdateOrgNamespaceVisibilityResponse,
  Error,
  { orgId: string; namespaceId: string; body: CustomerManagerUpdateNamespaceVisibilityRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, namespaceId, body }) =>
      updateCustomerManagerNamespaceVisibility(orgId, namespaceId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
    },
  });
}

export function useCustomerManagerWorkbench(
  scope: CustomerManagerWorkbenchScope,
): UseQueryResult<CustomerManagerWorkbenchResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: customerManagerWorkbenchQueryKey(scope),
    queryFn: ({ signal }) => getCustomerManagerWorkbench(scope, fetchImpl, signal),
  });
}

export function useCustomerManagerCustomerPool(
  scope: CustomerManagerWorkbenchScope,
): UseQueryResult<CustomerManagerCustomerPoolResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: customerManagerPoolQueryKey(scope),
    queryFn: ({ signal }) => getCustomerManagerCustomerPool(scope, fetchImpl, signal),
  });
}

export function useCustomerManagerCustomer360(
  orgId: string | null,
): UseQueryResult<CustomerManagerCustomer360Response> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY, 'customer-360', orgId] as const,
    queryFn: ({ signal }) => {
      if (orgId === null) {
        throw new Error('orgId is required');
      }
      return getCustomerManagerCustomer360(orgId, fetchImpl, signal);
    },
    enabled: orgId !== null,
  });
}

export function useCustomerManagerRequestLogs(
  orgId: string | null,
  scope: CustomerManagerRequestLogScope,
  enabled: boolean,
): UseQueryResult<CustomerManagerRequestLogResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey:
      orgId === null
        ? [...CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY, 'request-logs', null, scope]
        : customerManagerRequestLogsQueryKey(orgId, scope),
    queryFn: ({ signal }) => {
      if (orgId === null) {
        throw new Error('orgId is required');
      }
      return getCustomerManagerRequestLogs(orgId, scope, fetchImpl, signal);
    },
    enabled: orgId !== null && enabled,
  });
}

export function useCustomerManagerPortfolioRequestLogs(
  scope: CustomerManagerRequestLogScope,
): UseQueryResult<CustomerManagerPortfolioRequestLogResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: customerManagerPortfolioRequestLogsQueryKey(scope),
    queryFn: ({ signal }) => getCustomerManagerPortfolioRequestLogs(scope, fetchImpl, signal),
  });
}

export function useCustomerManagerRequestLifecycle(
  orgId: string,
  requestId: string,
  enabled: boolean,
): UseQueryResult<CustomerManagerRequestLifecycleResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY, 'request-lifecycle', orgId, requestId] as const,
    queryFn: ({ signal }) => getCustomerManagerRequestLifecycle(orgId, requestId, fetchImpl, signal),
    enabled: enabled && orgId.length > 0 && requestId.length > 0,
  });
}

export function customerManagerCreditLotsQueryKey(orgId: string): readonly unknown[] {
  return [...CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY, 'credit-lots', orgId] as const;
}

export function useCustomerManagerCreditLots(
  orgId: string,
  enabled: boolean,
): UseQueryResult<ListAdminCreditLotsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: customerManagerCreditLotsQueryKey(orgId),
    queryFn: ({ signal }) => getCustomerManagerCreditLots(orgId, fetchImpl, signal),
    enabled: enabled && orgId.length > 0,
  });
}

export function useClaimCustomerFromPool(): UseMutationResult<
  ClaimCustomerManagerResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId) => claimCustomerFromPool(orgId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_OWNERSHIP_ROOT_KEY });
    },
  });
}

export function useRevokeCustomerManagerApiKey(): UseMutationResult<
  CustomerManagerRevokeApiKeyResponse,
  Error,
  { orgId: string; keyId: string; body: CustomerManagerRevokeApiKeyRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, keyId, body }) =>
      revokeCustomerManagerApiKey(orgId, keyId, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
      void qc.invalidateQueries({
        queryKey: [...CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY, 'customer-360', vars.orgId],
      });
    },
  });
}

export function useIssueCustomerManagerGift(): UseMutationResult<
  CustomerManagerIssueGiftResponse,
  Error,
  { orgId: string; body: CustomerManagerIssueGiftRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => issueCustomerManagerGift(orgId, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: [...CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY, 'customer-360', vars.orgId],
      });
      // A gift that lands creates a lot; the table showing them is stale the
      // moment this resolves. (An over-quota gift creates an approval and no
      // lot, so the refetch is a no-op rather than wrong.)
      void qc.invalidateQueries({ queryKey: customerManagerCreditLotsQueryKey(vars.orgId) });
    },
  });
}

export function useRedeemCustomerManagerGiftCard(): UseMutationResult<
  CustomerManagerRedeemGiftCardResponse,
  Error,
  { orgId: string; body: CustomerManagerRedeemGiftCardRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => redeemCustomerManagerGiftCard(orgId, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: [...CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY, 'customer-360', vars.orgId],
      });
      void qc.invalidateQueries({ queryKey: customerManagerCreditLotsQueryKey(vars.orgId) });
    },
  });
}

export function useSetCustomerServiceSuspension(): UseMutationResult<
  SetServiceSuspensionResponse,
  Error,
  { orgId: string; body: SetServiceSuspensionRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => setCustomerServiceSuspension(orgId, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: [...CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY, 'customer-360', vars.orgId],
      });
    },
  });
}

export function useUpdateCustomerManagerSkuRate(): UseMutationResult<
  CustomerManagerUpdateSkuRateResponse,
  Error,
  { orgId: string; body: CustomerManagerUpdateSkuRateRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => updateCustomerManagerSkuRate(orgId, body, fetchImpl),
    onSuccess: (data, vars) => {
      void qc.invalidateQueries({
        queryKey: [...CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY, 'customer-360', vars.orgId],
      });
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
      if (data.outcome === 'approval_pending') {
        // The write converted into a pending discount_below_sales_cost
        // request (#1106): refresh the approvals surfaces (mine list,
        // per-row pending chips, queue count) in the same cycle.
        void qc.invalidateQueries({ queryKey: APPROVALS_ROOT_KEY });
      }
    },
  });
}

export function useUpdateCustomerManagerSkuVisibility(): UseMutationResult<
  CustomerManagerUpdateSkuVisibilityResponse,
  Error,
  { orgId: string; skuId: string; body: CustomerManagerUpdateSkuVisibilityRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, skuId, body }) =>
      updateCustomerManagerSkuVisibility(orgId, skuId, body, fetchImpl),
    onSuccess: () => {
      // The root invalidation already includes the customer-360 detail key.
      // Issuing both invalidations back-to-back cancels the first refetch and
      // surfaces a browser-level ERR_ABORTED even though the second succeeds.
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
    },
  });
}
